import { useCallback, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useAppDispatch } from '../hooks'
import {
    ListVendor,
    Company,
    ApartmentVendor,
    AreaConfig,
    UnitConfig,
    ServiceContract,
} from '../models'
import {
    createVendor,
    getApartmentList,
    getVendorList,
    removeVendorFromApt,
    RootState,
    setVendorList,
    updateApartment,
    updateVendor,
    updateVendorServices,
} from '../store'
import {
    CompanyDetailActionThunk,
    CreateVendorRequest,
    GetApartmentListActionThunk,
    GetApartmentListRequest,
    VendorListActionThunk,
    GetVendorListRequest,
    UpdateVendorRequest,
    UpdateVendorServicesRequest,
    VendorAndContractThunk,
    AddRemoveVendorAptRequest,
    GET_VENDOR_LIST_REQUEST,
    UpdateApartmentRequest,
    UpdatedPricingRequest,
    UpdatedVendorServicePricingRequest,
} from '../store/company/types'
import { axiosInstance } from '../helpers'
import { toast } from 'react-toastify'

type UseCompany = {
    apartmentList: Company[]
    vendorList: ListVendor[]
    vendorsLoading: boolean
    apartmentVendorList: ApartmentVendor[]
    apartmentVendorMap: Record<number, number[]>
    vendorPricing: VendorPricing | null // for use with the link that allows vendors to update their pricing
    vendorServiceContractList: ServiceContract[]
    getApartmentVendorMap: (organization?: number) => Promise<void>
    getVendorList: (req: GetVendorListRequest) => VendorListActionThunk
    getApartmentList: (
        req: GetApartmentListRequest,
    ) => GetApartmentListActionThunk
    createVendor: (req: CreateVendorRequest) => VendorAndContractThunk
    removeVendor: (req: AddRemoveVendorAptRequest) => VendorListActionThunk
    updateVendor: (req: UpdateVendorRequest) => VendorAndContractThunk
    updateVendorServices: (
        req: UpdateVendorServicesRequest,
    ) => CompanyDetailActionThunk
    updateApartment: (req: UpdateApartmentRequest) => Promise<Company>
    setVendorList: (vendorList: ListVendor[]) => void
    setApartmentVendorMap: (map: Record<number, number[]>) => void
    addVendorToApartment: (req: AddRemoveVendorAptRequest) => Promise<void>
    getApartmentVendorList: () => Promise<ApartmentVendor[]>
    getRecommendVendorsForProperty: () => Promise<{ vendors: ListVendor[] }>
    requestUpdatedPricing: (req: UpdatedPricingRequest) => Promise<void>
    fetchVendorPricing: (link_token: string) => Promise<any>
    setVendorPricing: (vendorPricing: VendorPricing | null) => void
    updateVendorPricing: (
        req: UpdatedVendorServicePricingRequest,
    ) => Promise<void>
    getVendorServiceContractList: (vendorId: number) => Promise<void>
    isSubmitted: boolean
    setIsSubmitted: (isSubmitted: boolean) => void
    acceptNewPricing: (vendorId: number) => Promise<void>
    rejectNewPricing: (vendorId: number) => Promise<void>
}

interface Options {
    getVendorList?: boolean
    cleanUp?: boolean
}

export const useCompany = (options?: Options): UseCompany => {
    const dispatch = useAppDispatch()
    const vendorList = useSelector(
        (state: RootState) => state.company.vendorList,
    )
    const apartmentList = useSelector(
        (state: RootState) => state.company.apartmentList,
    )

    const vendorsLoading = useSelector(
        (state: RootState) => state.company.isLoading[GET_VENDOR_LIST_REQUEST],
    )

    const [apartmentVendorMap, setApartmentVendorMap] = useState<
        Record<number, number[]>
    >({})
    const [apartmentVendorList, setApartmentVendorList] = useState<
        ApartmentVendor[]
    >([])

    const [vendorPricing, setVendorPricing] = useState<VendorPricing | null>(
        null,
    )

    const [vendorServiceContractList, setVendorServiceContractList] = useState<
        ServiceContract[]
    >([])
    const [isSubmitted, setIsSubmitted] = useState(false)

    useEffect(() => {
        if (options?.getVendorList) {
            dispatch(getVendorList({ params: { my_team: true } }))
        }

        return () => {
            if (options?.cleanUp) {
                dispatch(setVendorList())
            }
        }
    }, [])

    const getApartmentVendorList = async (): Promise<ApartmentVendor[]> => {
        return new Promise((resolve, reject) => {
            axiosInstance
                .get(`company/apartment_vendors/`)
                .then((res) => {
                    const apartmentVendorList: ApartmentVendor[] = res.data
                    setApartmentVendorList(apartmentVendorList)
                    resolve(apartmentVendorList)
                })
                .catch((e) => {
                    reject(e)
                })
        })
    }

    const getRecommendVendorsForProperty = useCallback(async (): Promise<{
        vendors: ListVendor[]
    }> => {
        const res = await axiosInstance.get<{ vendors: ListVendor[] }>(
            'company/vendor/recommend_vendors_for_property/',
        )
        return res.data
    }, [])

    const getApartmentVendorMap = async (
        organization?: number,
    ): Promise<void> => {
        return new Promise((resolve, reject) => {
            axiosInstance
                .get(`company/vendor/get_apartment_vendor_map/`, {
                    params: { organization: organization },
                })
                .then((res) => {
                    const map: Record<number, number[]> = res.data
                    setApartmentVendorMap(map)
                    resolve()
                })
                .catch((e) => {
                    reject(e)
                })
        })
    }

    const addVendorToApartment = async (
        req: AddRemoveVendorAptRequest,
    ): Promise<void> => {
        return new Promise((resolve, reject) => {
            axiosInstance
                .post(`company/apartment/add_vendor/`, req.body)
                .then((res) => {
                    if (req.body.apartment_id) {
                        setApartmentVendorMap({
                            ...apartmentVendorMap,
                            [req.body.apartment_id]: [
                                ...(apartmentVendorMap[req.body.apartment_id] ??
                                    []),
                                req.body.vendor,
                            ],
                        })
                    } else {
                        dispatch(setVendorList(res.data))
                    }
                    resolve()
                })
                .catch((e) => {
                    reject(e)
                })
        })
    }

    const requestUpdatedPricing = async (
        req: UpdatedPricingRequest,
    ): Promise<void> => {
        return new Promise((resolve, reject) => {
            axiosInstance
                .post(`company/apartment/request_updated_pricing/`, req.body)
                .then((res) => {
                    resolve()
                    toast.success('Pricing request sent to vendor')
                })
                .catch((e) => {
                    reject(e)
                })
        })
    }

    const fetchVendorPricing = async (link_token: string) => {
        await axiosInstance
            .get(`company/vendor_pricing_link/get_vendor_pricing/`, {
                params: {
                    link_token: link_token,
                },
            })
            .then((res) => {
                setVendorPricing(res.data)
            })
    }

    const updateVendorPricing = async (
        req: UpdatedVendorServicePricingRequest,
    ) => {
        await axiosInstance
            .post(
                `company/vendor_pricing_link/update_vendor_pricing/`,
                req.body,
            )
            .then((res) => {
                toast.success('Pricing updated')
                setVendorPricing(res.data)
                setIsSubmitted(true)
            })
            .catch((e) => {
                toast.error('Failed to update pricing')
            })
    }

    const getVendorServiceContractList = async (vendorId: number) => {
        await axiosInstance
            .get(`service-contract/get_vendor_submission/`, {
                params: { vendor: vendorId },
            })
            .then((res) => {
                setVendorServiceContractList(res.data)
            })
    }

    const acceptNewPricing = async (vendorId: number) => {
        await axiosInstance
            .post(`service-contract/accept_new_pricing/`, {
                vendor_id: vendorId,
            })
            .then((res) => {
                toast.success('Pricing accepted')
            })
            .catch((e) => {
                toast.error('Failed to accept pricing')
            })
    }

    const rejectNewPricing = async (vendorId: number) => {
        await axiosInstance
            .post(`service-contract/reject_new_pricing/`, {
                vendor_id: vendorId,
            })
            .then((res) => {
                toast.success('Pricing rejected')
            })
            .catch((e) => {
                toast.error('Failed to reject pricing')
            })
    }

    return {
        apartmentList,
        vendorList,
        vendorsLoading,
        apartmentVendorMap,
        apartmentVendorList,
        vendorPricing,
        vendorServiceContractList,
        isSubmitted,
        setIsSubmitted,
        getApartmentVendorMap,
        addVendorToApartment,
        getApartmentList: (req) => dispatch(getApartmentList(req)),
        getVendorList: (req) => dispatch(getVendorList(req)),
        createVendor: (req) => dispatch(createVendor(req)),
        removeVendor: (req) => dispatch(removeVendorFromApt(req)),
        updateVendor: (req) => dispatch(updateVendor(req)),
        updateVendorServices: (req) => dispatch(updateVendorServices(req)),
        updateApartment: (req) => updateApartment(req),
        setVendorList: (vendorList: ListVendor[]) =>
            dispatch(setVendorList(vendorList)),
        setApartmentVendorMap: (map: Record<number, number[]>) =>
            setApartmentVendorMap(map),
        getApartmentVendorList,
        getRecommendVendorsForProperty,
        requestUpdatedPricing,
        fetchVendorPricing,
        setVendorPricing,
        updateVendorPricing,
        getVendorServiceContractList,
        acceptNewPricing,
        rejectNewPricing,
    }
}

export interface VendorPricing {
    id: number
    apartment: number
    vendor: number
    service: number
    price: string
    area_config: AreaConfig
    unit_config: UnitConfig
    apartment_name: string
    vendor_name: string
    service_name: string
}

// Helper: Group VendorPricing[] by service_name
export function groupVendorPricingByService(
    pricingList: VendorPricing[],
): Record<string, VendorPricing[]> {
    return pricingList.reduce((acc, item) => {
        if (!acc[item.service_name]) {
            acc[item.service_name] = []
        }
        acc[item.service_name].push(item)
        return acc
    }, {} as Record<string, VendorPricing[]>)
}

// Helper: Get area pricing (area_config not null)
export function getAreaPricingForService(
    pricingList: VendorPricing[],
): VendorPricing[] {
    return pricingList.filter((item) => item.area_config != null)
}

// Helper: Get unit pricing (unit_config not null)
export function getUnitPricingForService(
    pricingList: VendorPricing[],
): VendorPricing[] {
    return pricingList.filter((item) => item.unit_config != null)
}
